import { useState, useEffect } from 'react';
import { apiClient } from '../utils/apiClient';

interface UseApiDataOptions {
  endpoint: string;
  defaultData?: any;
  refreshInterval?: number;
}

interface UseApiDataResult<T> {
  data: T;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useApiData<T = any>({
  endpoint,
  defaultData = null,
  refreshInterval
}: UseApiDataOptions): UseApiDataResult<T> {
  const [data, setData] = useState<T>(defaultData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await apiClient.get<T>(endpoint);
      
      if (result.error) {
        setError(result.error);
        setData(defaultData);
      } else {
        setData(result.data);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      setData(defaultData);
    } finally {
      setLoading(false);
    }
  };

  const refetch = () => {
    fetchData();
  };

  useEffect(() => {
    fetchData();

    if (refreshInterval && refreshInterval > 0) {
      const interval = setInterval(fetchData, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [endpoint, refreshInterval]);

  return {
    data,
    loading,
    error,
    refetch,
  };
}

// Hook specifically for device data with fallbacks
export function useDeviceData() {
  const connectorsResult = useApiData({
    endpoint: '/v1/devices/connectors/',
    defaultData: { results: [] },
  });

  const analyzersResult = useApiData({
    endpoint: '/v1/devices/analyzers/',
    defaultData: { results: [] },
  });

  return {
    connectors: connectorsResult.data?.results || connectorsResult.data?.data || [],
    analyzers: analyzersResult.data?.results || analyzersResult.data?.data || [],
    loading: connectorsResult.loading || analyzersResult.loading,
    error: connectorsResult.error || analyzersResult.error,
    refetch: () => {
      connectorsResult.refetch();
      analyzersResult.refetch();
    },
  };
}
